import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import { AUTOTRADE_STRATEGY_OPTIONS } from '../constants/autotradeStrategies';
import {
  DEFAULT_STRATEGY_EXIT_CONFIG,
  mergeStrategyExitConfig,
} from '../constants/autotradeStrategyExitDefaults';

function buildAutoTradeConfigPayload({
  autoTradeLots,
  autoTradeStrikeMode,
  autoTradeExpiryChoice,
  autoTradeAllStrategies,
  autoTradeSelectedStrategies,
  autoTradeExitSource,
  autoTradeSignalTargetKey,
  autoTradeCustomTargetPrice,
  autoTradeCustomStopLossPrice,
  autoTradeStrategyExitConfig,
  autoTradeOptionSideFilter = 'BOTH',
  autoTradeProfitLimitInr = '',
  autoTradeLossLimitInr = '',
}) {
  const strategy_types =
    autoTradeAllStrategies || autoTradeSelectedStrategies.length === 0
      ? null
      : [...autoTradeSelectedStrategies];

  const hasCustomTarget = autoTradeCustomTargetPrice !== '';
  const hasCustomStopLoss = autoTradeCustomStopLossPrice !== '';
  if (hasCustomTarget !== hasCustomStopLoss) {
    return { error: 'Please provide both Custom Target Price and Custom Stop-Loss Price, or leave both empty.' };
  }

  const customTarget =
    autoTradeCustomTargetPrice !== '' && Number.isFinite(Number(autoTradeCustomTargetPrice))
      ? Number(autoTradeCustomTargetPrice)
      : null;
  const customStopLoss =
    autoTradeCustomStopLossPrice !== '' && Number.isFinite(Number(autoTradeCustomStopLossPrice))
      ? Number(autoTradeCustomStopLossPrice)
      : null;
  if (customTarget != null && customStopLoss != null && customTarget <= customStopLoss) {
    return { error: 'Custom Target Price must be greater than Custom Stop-Loss Price.' };
  }

  const activeKeys = autoTradeAllStrategies
    ? AUTOTRADE_STRATEGY_OPTIONS.map((o) => o.value)
    : autoTradeSelectedStrategies;
  const strategy_exit_config = {};
  for (const key of activeKeys) {
    const cfg = autoTradeStrategyExitConfig[key] || DEFAULT_STRATEGY_EXIT_CONFIG[key];
    if (!cfg) continue;
    strategy_exit_config[key] = {
      target_points: Math.max(1, Math.min(500, Number(cfg.target_points) || 10)),
      stop_loss_points: Math.max(1, Math.min(500, Number(cfg.stop_loss_points) || 10)),
      trailing_enabled: Boolean(cfg.trailing_enabled),
    };
  }
  const fallbackCfg = strategy_exit_config[activeKeys[0]] || {
    target_points: 10,
    stop_loss_points: 10,
  };

  return {
    payload: {
      target_points: fallbackCfg.target_points,
      stop_loss_points: fallbackCfg.stop_loss_points,
      quantity: autoTradeLots,
      strike_mode: autoTradeStrikeMode,
      expiry_choice: autoTradeExpiryChoice,
      strategy_types,
      exit_source: autoTradeExitSource,
      signal_target_key: autoTradeSignalTargetKey,
      custom_target_price: customTarget,
      custom_stop_loss_price: customStopLoss,
      clear_custom_prices: customTarget == null && customStopLoss == null,
      strategy_exit_config,
      reapply_open_orders: true,
      option_side_filter: autoTradeOptionSideFilter || 'BOTH',
      session_profit_limit_inr:
        autoTradeProfitLimitInr !== '' && Number.isFinite(Number(autoTradeProfitLimitInr))
          ? Math.max(1, Number(autoTradeProfitLimitInr))
          : null,
      session_loss_limit_inr:
        autoTradeLossLimitInr !== '' && Number.isFinite(Number(autoTradeLossLimitInr))
          ? Math.max(1, Number(autoTradeLossLimitInr))
          : null,
      clear_session_profit_limit: autoTradeProfitLimitInr === '',
      clear_session_loss_limit: autoTradeLossLimitInr === '',
    },
  };
}

/**
 * Auto-trade session + configuration (same API contract as NiftySignalAnalyzerPage).
 */
export function useAutoTrade({ user, hasActiveSubscription }) {
  const [autoTradeEnabled, setAutoTradeEnabled] = useState(false);
  const [autoTradeStatus, setAutoTradeStatus] = useState(null);
  const [autoTradeLoading, setAutoTradeLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [autoTradeLots, setAutoTradeLots] = useState(1);
  const [autoTradeExitSource, setAutoTradeExitSource] = useState('session_points');
  const [autoTradeSignalTargetKey, setAutoTradeSignalTargetKey] = useState('target_1');
  const [autoTradeCustomTargetPrice, setAutoTradeCustomTargetPrice] = useState('');
  const [autoTradeCustomStopLossPrice, setAutoTradeCustomStopLossPrice] = useState('');
  const [autoTradeStrategyExitConfig, setAutoTradeStrategyExitConfig] = useState(() => ({
    ...DEFAULT_STRATEGY_EXIT_CONFIG,
  }));
  const [autoTradeOptionSideFilter, setAutoTradeOptionSideFilter] = useState('BOTH');
  const [autoTradeProfitLimitInr, setAutoTradeProfitLimitInr] = useState('');
  const [autoTradeLossLimitInr, setAutoTradeLossLimitInr] = useState('');
  const [autoTradeStrikeMode, setAutoTradeStrikeMode] = useState('ATM');
  const [autoTradeExpiryChoice, setAutoTradeExpiryChoice] = useState('AUTO');
  /** When true, all NIFTY strategies may trigger auto-trade (strategy_types omitted in API). */
  const [autoTradeAllStrategies, setAutoTradeAllStrategies] = useState(true);
  /** strategy_type values when autoTradeAllStrategies is false */
  const [autoTradeSelectedStrategies, setAutoTradeSelectedStrategies] = useState([]);
  const [autoTradeDisabledReason, setAutoTradeDisabledReason] = useState('');
  const [showAutoTradeDisabledModal, setShowAutoTradeDisabledModal] = useState(false);
  const [autoTradeModalCtaLabel, setAutoTradeModalCtaLabel] = useState(null);
  const [actionError, setActionError] = useState('');
  const [configSaving, setConfigSaving] = useState(false);

  const configInputs = useMemo(
    () => ({
      autoTradeLots,
      autoTradeStrikeMode,
      autoTradeExpiryChoice,
      autoTradeAllStrategies,
      autoTradeSelectedStrategies,
      autoTradeExitSource,
      autoTradeSignalTargetKey,
      autoTradeCustomTargetPrice,
      autoTradeCustomStopLossPrice,
      autoTradeStrategyExitConfig,
      autoTradeOptionSideFilter,
      autoTradeProfitLimitInr,
      autoTradeLossLimitInr,
    }),
    [
      autoTradeLots,
      autoTradeStrikeMode,
      autoTradeExpiryChoice,
      autoTradeAllStrategies,
      autoTradeSelectedStrategies,
      autoTradeExitSource,
      autoTradeSignalTargetKey,
      autoTradeCustomTargetPrice,
      autoTradeCustomStopLossPrice,
      autoTradeStrategyExitConfig,
      autoTradeOptionSideFilter,
      autoTradeProfitLimitInr,
      autoTradeLossLimitInr,
    ],
  );

  const updateStrategyExitField = useCallback((strategyKey, field, value) => {
    const key = String(strategyKey).toLowerCase();
    setAutoTradeStrategyExitConfig((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  }, []);

  const fetchAutoTradeStatus = useCallback(async () => {
    try {
      const resp = await api.get('/api/v1/auto-trade/status');
      if (resp.data) {
        setAutoTradeEnabled(resp.data.is_active);
        setAutoTradeStatus(resp.data);
        if (resp.data.exit_source) setAutoTradeExitSource(resp.data.exit_source);
        if (resp.data.signal_target_key) setAutoTradeSignalTargetKey(resp.data.signal_target_key);
        setAutoTradeCustomTargetPrice(
          resp.data.custom_target_price != null ? String(resp.data.custom_target_price) : ''
        );
        setAutoTradeCustomStopLossPrice(
          resp.data.custom_stop_loss_price != null ? String(resp.data.custom_stop_loss_price) : ''
        );
        if (resp.data.strategy_exit_config) {
          setAutoTradeStrategyExitConfig(mergeStrategyExitConfig(resp.data.strategy_exit_config));
        }
        if (resp.data.option_side_filter) {
          setAutoTradeOptionSideFilter(String(resp.data.option_side_filter).toUpperCase());
        }
        setAutoTradeProfitLimitInr(
          resp.data.session_profit_limit_inr != null ? String(resp.data.session_profit_limit_inr) : ''
        );
        setAutoTradeLossLimitInr(
          resp.data.session_loss_limit_inr != null ? String(resp.data.session_loss_limit_inr) : ''
        );
        if (resp.data.is_active) {
          if (resp.data.quantity != null) setAutoTradeLots(resp.data.quantity);
          if (resp.data.strike_mode) setAutoTradeStrikeMode(resp.data.strike_mode);
          if (resp.data.expiry_choice) setAutoTradeExpiryChoice(resp.data.expiry_choice);
          const st = resp.data.strategy_types;
          if (Array.isArray(st) && st.length > 0) {
            setAutoTradeAllStrategies(false);
            const allowed = new Set(AUTOTRADE_STRATEGY_OPTIONS.map((o) => o.value));
            setAutoTradeSelectedStrategies(st.filter((x) => allowed.has(String(x).toLowerCase())));
          } else {
            setAutoTradeAllStrategies(true);
            setAutoTradeSelectedStrategies([]);
          }
        }
      }
    } catch (e) {
      console.error('Error fetching auto-trade status:', e);
      setAutoTradeEnabled(false);
      setAutoTradeStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAutoTradeStatus();
    const ms = autoTradeEnabled ? 10000 : 30000;
    const interval = setInterval(() => {
      fetchAutoTradeStatus();
    }, ms);
    return () => clearInterval(interval);
  }, [fetchAutoTradeStatus, autoTradeEnabled]);

  const startAutoTrade = useCallback(async () => {
    setAutoTradeLoading(true);
    setActionError('');
    try {
      const built = buildAutoTradeConfigPayload(configInputs);
      if (built.error) {
        setActionError(built.error);
        return;
      }

      const { clear_custom_prices: _c, reapply_open_orders: _r, clear_session_profit_limit: _cp, clear_session_loss_limit: _cl, ...startPayload } = built.payload;

      const resp = await api.post('/api/v1/auto-trade/start', {
        broker: 'ZERODHA',
        ...startPayload,
      });

      if (resp.data && resp.data.success) {
        setAutoTradeEnabled(true);
        setAutoTradeStatus(resp.data);
      }
    } catch (e) {
      const errorMsg = e?.response?.data?.detail || e.message || 'Failed to start automated trading';
      const lower = (errorMsg || '').toLowerCase();
      const isSubscriptionError =
        lower.includes('active subscription') ||
        lower.includes('requires an active subscription') ||
        lower.includes('expert subscription') ||
        lower.includes('expert plan') ||
        lower.includes('subscription required');

      if (isSubscriptionError) {
        const hasPurchasedBefore = Boolean(user?.subscription_plan || user?.subscription_valid_until);
        const showRestore = hasPurchasedBefore && !hasActiveSubscription;
        setAutoTradeModalCtaLabel(showRestore ? 'Restore Access' : 'Buy Expert Plan');
        setAutoTradeDisabledReason(
          'An active subscription is required to access this feature. Please subscribe to a plan.'
        );
        setShowAutoTradeDisabledModal(true);
        return;
      }

      if (
        lower.includes('automated trading is currently disabled for all users') ||
        lower.includes('automated trading is disabled for your account') ||
        lower.includes('market is closed') ||
        lower.includes('only available during market hours')
      ) {
        setAutoTradeModalCtaLabel(null);
        setAutoTradeDisabledReason(errorMsg);
        setShowAutoTradeDisabledModal(true);
      } else {
        setActionError(errorMsg);
      }
    } finally {
      setAutoTradeLoading(false);
    }
  }, [
    configInputs,
    user,
    hasActiveSubscription,
  ]);

  const saveAutoTradeConfig = useCallback(async () => {
    setConfigSaving(true);
    setActionError('');
    try {
      const built = buildAutoTradeConfigPayload(configInputs);
      if (built.error) {
        setActionError(built.error);
        return { ok: false };
      }

      const resp = await api.patch('/api/v1/auto-trade/config', built.payload, {
        params: { broker: 'ZERODHA' },
      });

      if (resp.data?.success) {
        await fetchAutoTradeStatus();
        return {
          ok: true,
          openOrdersUpdated: resp.data.open_orders_updated || 0,
          openOrdersSkipped: resp.data.open_orders_skipped || 0,
        };
      }
      setActionError(resp.data?.message || 'Failed to save configuration');
      return { ok: false };
    } catch (e) {
      const errorMsg = e?.response?.data?.detail || e.message || 'Failed to save configuration';
      setActionError(errorMsg);
      return { ok: false };
    } finally {
      setConfigSaving(false);
    }
  }, [configInputs, fetchAutoTradeStatus]);

  const stopAutoTrade = useCallback(async () => {
    setAutoTradeLoading(true);
    setActionError('');
    try {
      const resp = await api.post('/api/v1/auto-trade/stop', null, {
        params: { broker: 'ZERODHA' },
      });

      if (resp.data && resp.data.success) {
        setAutoTradeEnabled(false);
        setAutoTradeStatus(null);
      }
    } catch (e) {
      const errorMsg = e?.response?.data?.detail || e.message || 'Failed to stop automated trading';
      setActionError(errorMsg);
    } finally {
      setAutoTradeLoading(false);
    }
  }, []);

  const toggleAutomate = useCallback(async () => {
    if (autoTradeEnabled) {
      await stopAutoTrade();
    } else {
      await startAutoTrade();
    }
  }, [autoTradeEnabled, startAutoTrade, stopAutoTrade]);

  const toggleAutoTradeStrategy = useCallback((value) => {
    const v = String(value).toLowerCase();
    setAutoTradeSelectedStrategies((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
    );
  }, []);

  return {
    autoTradeEnabled,
    autoTradeStatus,
    autoTradeLoading,
    statusLoading,
    autoTradeLots,
    setAutoTradeLots,
    autoTradeExitSource,
    setAutoTradeExitSource,
    autoTradeSignalTargetKey,
    setAutoTradeSignalTargetKey,
    autoTradeCustomTargetPrice,
    setAutoTradeCustomTargetPrice,
    autoTradeCustomStopLossPrice,
    setAutoTradeCustomStopLossPrice,
    autoTradeStrategyExitConfig,
    updateStrategyExitField,
    autoTradeOptionSideFilter,
    setAutoTradeOptionSideFilter,
    autoTradeProfitLimitInr,
    setAutoTradeProfitLimitInr,
    autoTradeLossLimitInr,
    setAutoTradeLossLimitInr,
    autoTradeStrikeMode,
    setAutoTradeStrikeMode,
    autoTradeExpiryChoice,
    setAutoTradeExpiryChoice,
    autoTradeAllStrategies,
    setAutoTradeAllStrategies,
    autoTradeSelectedStrategies,
    setAutoTradeSelectedStrategies,
    toggleAutoTradeStrategy,
    fetchAutoTradeStatus,
    startAutoTrade,
    saveAutoTradeConfig,
    configSaving,
    stopAutoTrade,
    toggleAutomate,
    actionError,
    setActionError,
    autoTradeDisabledReason,
    showAutoTradeDisabledModal,
    setShowAutoTradeDisabledModal,
    autoTradeModalCtaLabel,
  };
}
